import React, { useState, useEffect, useCallback } from "react";
import {
  RotateCcw,
  Lightbulb,
  Trophy,
  Minus,
  MousePointer,
  Grid3X3,
  Square,
  Columns,
  Rows,
  CheckCircle,
  XCircle,
  Calculator,
  Target,
  TrendingDown,
} from "lucide-react";

const AMCSimulatorSuite = () => {
  const [activeTab, setActiveTab] = useState("circle");

  // ===== 环形差值游戏组件 =====
  const CircleDifferenceGame = () => {
    const [circles, setCircles] = useState(Array(7).fill(null));
    const [selectedNumber, setSelectedNumber] = useState(null);
    const [availableNumbers, setAvailableNumbers] = useState([
      1, 2, 3, 4, 5, 6, 7,
    ]);
    const [differences, setDifferences] = useState([]);
    const [totalSum, setTotalSum] = useState(0);
    const [showOptimal, setShowOptimal] = useState(false);

    const getCirclePosition = (index, total = 7, radius = 120) => {
      const angle = (2 * Math.PI * index) / total - Math.PI / 2;
      const x = 200 + radius * Math.cos(angle);
      const y = 200 + radius * Math.sin(angle);
      return { x, y, angle };
    };

    const calculateCounts = useCallback(() => {
      const newDifferences = [];
      let sum = 0;

      for (let i = 0; i < 7; i++) {
        const current = circles[i];
        const next = circles[(i + 1) % 7];

        if (current !== null && next !== null) {
          const diff = Math.abs(current - next);
          newDifferences[i] = diff;
          sum += diff;
        } else {
          newDifferences[i] = null;
        }
      }

      setDifferences(newDifferences);
      setTotalSum(sum);
    }, [circles]);

    useEffect(() => {
      calculateCounts();
    }, [calculateCounts]);

    const handleCircleClick = (index) => {
      if (selectedNumber !== null && circles[index] === null) {
        const newCircles = [...circles];
        newCircles[index] = selectedNumber;
        setCircles(newCircles);

        const newAvailable = availableNumbers.filter(
          (n) => n !== selectedNumber
        );
        setAvailableNumbers(newAvailable);
        setSelectedNumber(null);
      } else if (circles[index] !== null) {
        const removedNumber = circles[index];
        const newCircles = [...circles];
        newCircles[index] = null;
        setCircles(newCircles);

        const newAvailable = [...availableNumbers, removedNumber].sort(
          (a, b) => a - b
        );
        setAvailableNumbers(newAvailable);
      }
    };

    const resetGame = () => {
      setCircles(Array(7).fill(null));
      setAvailableNumbers([1, 2, 3, 4, 5, 6, 7]);
      setSelectedNumber(null);
      setShowOptimal(false);
    };

    const showOptimalSolution = () => {
      const optimal = [1, 7, 2, 6, 3, 5, 4];
      setCircles(optimal);
      setAvailableNumbers([]);
      setSelectedNumber(null);
      setShowOptimal(true);
    };

    const getEdgePosition = (index) => {
      const pos1 = getCirclePosition(index);
      const pos2 = getCirclePosition((index + 1) % 7);
      return {
        x: (pos1.x + pos2.x) / 2,
        y: (pos1.y + pos2.y) / 2,
      };
    };

    const isGameComplete = circles.every((c) => c !== null);
    const maxPossibleSum = 24;

    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h3 className="text-xl font-bold text-gray-800 mb-4">
            7个圆圈环形排列
          </h3>
          <div className="relative">
            <svg
              width="400"
              height="400"
              viewBox="0 0 400 400"
              className="mx-auto"
            >
              {Array.from({ length: 7 }).map((_, i) => {
                const pos1 = getCirclePosition(i);
                const pos2 = getCirclePosition((i + 1) % 7);
                const hasValues =
                  circles[i] !== null && circles[(i + 1) % 7] !== null;

                return (
                  <line
                    key={`line-${i}`}
                    x1={pos1.x}
                    y1={pos1.y}
                    x2={pos2.x}
                    y2={pos2.y}
                    stroke={hasValues ? "#3b82f6" : "#e5e7eb"}
                    strokeWidth={hasValues ? "3" : "2"}
                    strokeDasharray={hasValues ? "none" : "5,5"}
                  />
                );
              })}

              {Array.from({ length: 7 }).map((_, i) => {
                const pos = getCirclePosition(i);
                const isEmpty = circles[i] === null;

                return (
                  <g key={`circle-${i}`}>
                    <circle
                      cx={pos.x}
                      cy={pos.y}
                      r="30"
                      fill={
                        isEmpty
                          ? "#f8fafc"
                          : showOptimal
                          ? "#10b981"
                          : "#3b82f6"
                      }
                      stroke={isEmpty ? "#cbd5e1" : "#1e40af"}
                      strokeWidth="3"
                      className="cursor-pointer transition-all duration-200 hover:stroke-indigo-600"
                      onClick={() => handleCircleClick(i)}
                    />
                    {!isEmpty && (
                      <text
                        x={pos.x}
                        y={pos.y + 5}
                        textAnchor="middle"
                        className="text-white text-xl font-bold pointer-events-none select-none"
                      >
                        {circles[i]}
                      </text>
                    )}
                  </g>
                );
              })}

              {differences.map((diff, i) => {
                if (diff === null) return null;
                const edgePos = getEdgePosition(i);

                return (
                  <g key={`diff-${i}`}>
                    <circle
                      cx={edgePos.x}
                      cy={edgePos.y}
                      r="15"
                      fill="#fbbf24"
                      stroke="#f59e0b"
                      strokeWidth="2"
                    />
                    <text
                      x={edgePos.x}
                      y={edgePos.y + 4}
                      textAnchor="middle"
                      className="text-white text-sm font-bold pointer-events-none"
                    >
                      {diff}
                    </text>
                  </g>
                );
              })}
            </svg>
          </div>
        </div>

        <div className="space-y-4">
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-xl font-bold text-gray-800 mb-4">可用数字</h3>
            <div className="grid grid-cols-4 gap-3">
              {availableNumbers.map((num) => (
                <button
                  key={num}
                  onClick={() =>
                    setSelectedNumber(selectedNumber === num ? null : num)
                  }
                  className={`w-12 h-12 rounded-lg font-bold text-lg transition-all duration-200 ${
                    selectedNumber === num
                      ? "bg-blue-500 text-white transform scale-110"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  {num}
                </button>
              ))}
            </div>
            {selectedNumber && (
              <p className="text-sm text-blue-600 mt-3">
                点击空圆圈放置数字 {selectedNumber}
              </p>
            )}
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
              <Trophy className="text-yellow-500" />
              得分统计
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">当前总和:</span>
                <span className="text-2xl font-bold text-blue-600">
                  {totalSum}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">最大可能:</span>
                <span className="text-lg font-semibold text-gray-800">
                  {maxPossibleSum}
                </span>
              </div>
              {isGameComplete && (
                <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                  <div className="text-center">
                    {totalSum === maxPossibleSum ? (
                      <p className="text-green-600 font-bold">
                        🎉 完美！达到最大值！
                      </p>
                    ) : (
                      <p className="text-orange-600">
                        还有 {maxPossibleSum - totalSum} 分的提升空间
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-xl font-bold text-gray-800 mb-4">操作</h3>
            <div className="space-y-3">
              <button
                onClick={resetGame}
                className="w-full flex items-center justify-center gap-2 bg-gray-500 text-white py-3 px-4 rounded-lg hover:bg-gray-600 transition-colors"
              >
                <RotateCcw size={20} />
                重新开始
              </button>
              <button
                onClick={showOptimalSolution}
                className="w-full flex items-center justify-center gap-2 bg-green-500 text-white py-3 px-4 rounded-lg hover:bg-green-600 transition-colors"
              >
                <Lightbulb size={20} />
                显示最优解
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ===== 红色高帮鞋分配模拟器 =====
  const ShoeAllocationSimulator = () => {
    const [shoes, setShoes] = useState(() => {
      return Array.from({ length: 15 }, (_, index) => ({
        id: index,
        isHigh: index < 10,
        isRed: false,
      }));
    });

    const MAX_RED_SHOES = 9;
    const redShoes = shoes.filter((shoe) => shoe.isRed);
    const redHighShoes = shoes.filter((shoe) => shoe.isRed && shoe.isHigh);
    const redLowShoes = shoes.filter((shoe) => shoe.isRed && !shoe.isHigh);
    const whiteHighShoes = shoes.filter((shoe) => !shoe.isRed && shoe.isHigh);
    const whiteLowShoes = shoes.filter((shoe) => !shoe.isRed && !shoe.isHigh);
    const remainingRedToAssign = MAX_RED_SHOES - redShoes.length;

    const handleShoeClick = (shoeId) => {
      setShoes((prevShoes) => {
        return prevShoes.map((shoe) => {
          if (shoe.id === shoeId) {
            if (shoe.isRed) {
              return { ...shoe, isRed: false };
            } else if (remainingRedToAssign > 0) {
              return { ...shoe, isRed: true };
            }
          }
          return shoe;
        });
      });
    };

    const resetShoes = () => {
      setShoes((prevShoes) =>
        prevShoes.map((shoe) => ({ ...shoe, isRed: false }))
      );
    };

    const showOptimalAllocation = () => {
      setShoes((prevShoes) =>
        prevShoes.map((shoe, index) => ({
          ...shoe,
          isRed: !shoe.isHigh || (shoe.isHigh && index < 4),
        }))
      );
    };

    const renderShoe = (shoe) => {
      const isClickable = !shoe.isRed || remainingRedToAssign >= 0;

      return (
        <div
          key={shoe.id}
          onClick={() => handleShoeClick(shoe.id)}
          className={`relative cursor-pointer transition-all duration-200 transform hover:scale-110 ${
            isClickable ? "hover:shadow-lg" : ""
          }`}
          title={`${shoe.isRed ? "红色" : "白色"}${
            shoe.isHigh ? "高帮" : "低帮"
          }鞋 (点击切换颜色)`}
        >
          <div
            className={`w-16 rounded-lg border-2 relative ${
              shoe.isHigh ? "h-12" : "h-8"
            } ${
              shoe.isRed
                ? "bg-red-500 border-red-600"
                : "bg-gray-100 border-gray-300"
            }`}
          >
            {shoe.isHigh && (
              <div
                className={`absolute -top-4 left-1 right-1 h-8 rounded-t-lg border-2 ${
                  shoe.isRed
                    ? "bg-red-600 border-red-700"
                    : "bg-gray-200 border-gray-400"
                }`}
              />
            )}

            <div
              className={`absolute left-1/2 transform -translate-x-1/2 w-2 h-1 bg-black rounded-full opacity-70 ${
                shoe.isHigh ? "top-2" : "top-1"
              }`}
            />

            <div className="absolute -bottom-1 left-0 right-0 h-2 bg-white rounded-lg border border-gray-300" />

            {shoe.isRed && (
              <div
                className={`absolute -right-1 w-4 h-4 bg-yellow-400 rounded-full border-2 border-white flex items-center justify-center ${
                  shoe.isHigh ? "-top-3" : "-top-1"
                }`}
              >
                <div className="w-2 h-2 bg-red-600 rounded-full" />
              </div>
            )}
          </div>

          <div className="text-center mt-1 text-xs font-medium text-gray-600">
            {shoe.id + 1}
          </div>

          <div
            className={`text-center text-xs font-bold ${
              shoe.isHigh ? "text-purple-600" : "text-green-600"
            }`}
          >
            {shoe.isHigh ? "高帮" : "低帮"}
          </div>
        </div>
      );
    };

    return (
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2 bg-white rounded-xl shadow-lg p-6">
          <h3 className="text-xl font-bold text-gray-800 mb-4">
            Jordan的15双鞋子
          </h3>

          <div className="mb-4 p-4 bg-red-50 rounded-lg border border-red-200">
            <div className="flex items-center justify-between">
              <div>
                <span className="text-red-800 font-medium">红色鞋子分配：</span>
                <span className="text-2xl font-bold text-red-600 ml-2">
                  {redShoes.length} / {MAX_RED_SHOES}
                </span>
              </div>
              {remainingRedToAssign > 0 ? (
                <div className="text-orange-600 font-medium">
                  还需分配 {remainingRedToAssign} 双红色鞋
                </div>
              ) : (
                <div className="text-green-600 font-medium">
                  ✅ 红色鞋子已全部分配
                </div>
              )}
            </div>
          </div>

          <div className="bg-gray-50 p-6 rounded-lg">
            <div className="text-center mb-4">
              <div className="text-sm text-gray-600 mb-2">
                点击鞋子切换红色/白色
              </div>
              <div className="flex justify-center gap-4 text-xs">
                <span className="flex items-center gap-1">
                  <div className="w-3 h-3 bg-purple-500 rounded" />
                  高帮鞋 (前10双)
                </span>
                <span className="flex items-center gap-1">
                  <div className="w-3 h-3 bg-green-500 rounded" />
                  低帮鞋 (后5双)
                </span>
              </div>
            </div>

            <div className="grid grid-cols-5 md:grid-cols-10 lg:grid-cols-15 gap-4 justify-items-center">
              {shoes.map((shoe) => renderShoe(shoe))}
            </div>
          </div>

          <div className="mt-6 flex flex-wrap gap-3 justify-center">
            <button
              onClick={resetShoes}
              className="flex items-center gap-2 bg-gray-500 text-white py-3 px-6 rounded-lg hover:bg-gray-600 transition-colors font-medium"
            >
              <RotateCcw size={20} />
              重置所有
            </button>
            <button
              onClick={showOptimalAllocation}
              className="flex items-center gap-2 bg-green-500 text-white py-3 px-6 rounded-lg hover:bg-green-600 transition-colors font-medium"
            >
              <Lightbulb size={20} />
              显示最优分配
            </button>
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
              <Target className="text-blue-500" />
              当前统计
            </h3>

            <div className="space-y-3">
              <div className="flex justify-between items-center p-3 bg-red-50 rounded-lg">
                <span className="font-medium text-red-700">红色高帮鞋</span>
                <span className="text-2xl font-bold text-red-600">
                  {redHighShoes.length}
                </span>
              </div>
              <div className="flex justify-between items-center p-3 bg-red-50 rounded-lg">
                <span className="font-medium text-red-700">红色低帮鞋</span>
                <span className="text-2xl font-bold text-red-600">
                  {redLowShoes.length}
                </span>
              </div>
              <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                <span className="font-medium text-gray-700">白色高帮鞋</span>
                <span className="text-2xl font-bold text-gray-600">
                  {whiteHighShoes.length}
                </span>
              </div>
              <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                <span className="font-medium text-gray-700">白色低帮鞋</span>
                <span className="text-2xl font-bold text-gray-600">
                  {whiteLowShoes.length}
                </span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
              <TrendingDown className="text-orange-500" />
              目标分析
            </h3>

            <div className="space-y-4">
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <div className="text-orange-800 font-medium mb-2">
                  当前红色高帮鞋数量
                </div>
                <div className="text-3xl font-bold text-orange-600">
                  {redHighShoes.length}双
                </div>
              </div>

              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <div className="text-green-800 font-medium mb-2">
                  理论最少数量
                </div>
                <div className="text-3xl font-bold text-green-600">4双</div>
              </div>

              {redShoes.length === MAX_RED_SHOES && (
                <div
                  className={`p-3 rounded-lg border ${
                    redHighShoes.length === 4
                      ? "bg-green-100 border-green-300"
                      : redHighShoes.length < 4
                      ? "bg-red-100 border-red-300"
                      : "bg-yellow-100 border-yellow-300"
                  }`}
                >
                  {redHighShoes.length === 4 ? (
                    <div className="text-green-800">🎉 恭喜！达到最优解！</div>
                  ) : redHighShoes.length < 4 ? (
                    <div className="text-red-800">
                      ❌ 无效分配：红色鞋总数不足9双
                    </div>
                  ) : (
                    <div className="text-yellow-800">
                      ⚠️ 可以进一步优化：比最优解多 {redHighShoes.length - 4} 双
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ===== 字母网格统计器 =====
  const LetterGridCounter = () => {
    const generateGrid = () => {
      const letters = ["P", "Q", "R"];
      const grid = [];

      for (let row = 0; row < 20; row++) {
        const currentRow = [];
        for (let col = 0; col < 20; col++) {
          const letterIndex = (col + 1 - row + 60) % 3;
          currentRow.push(letters[letterIndex]);
        }
        grid.push(currentRow);
      }
      return grid;
    };

    const [grid] = useState(generateGrid());
    const [selectedCells, setSelectedCells] = useState(new Set());
    const [selectionMode, setSelectionMode] = useState("cell");
    const [isSelecting, setIsSelecting] = useState(false);
    const [selectionStart, setSelectionStart] = useState(null);
    const [counts, setCounts] = useState({ P: 0, Q: 0, R: 0 });
    const [canceledCounts, setCanceledCounts] = useState({ P: 0, Q: 0, R: 0 });

    const calculateCounts = useCallback(() => {
      const newCounts = { P: 0, Q: 0, R: 0 };
      selectedCells.forEach((key) => {
        const [row, col] = key.split("-").map(Number);
        const letter = grid[row][col];
        newCounts[letter]++;
      });
      setCounts(newCounts);
    }, [selectedCells, grid]);

    useEffect(() => {
      calculateCounts();
    }, [calculateCounts]);

    const handleCellClick = (row, col) => {
      const key = `${row}-${col}`;

      if (selectionMode === "cell") {
        const newSelected = new Set(selectedCells);
        if (newSelected.has(key)) {
          newSelected.delete(key);
        } else {
          newSelected.add(key);
        }
        setSelectedCells(newSelected);
      } else if (selectionMode === "row") {
        const newSelected = new Set(selectedCells);
        let hasAnyInRow = false;

        for (let c = 0; c < 20; c++) {
          if (newSelected.has(`${row}-${c}`)) {
            hasAnyInRow = true;
            break;
          }
        }

        for (let c = 0; c < 20; c++) {
          const cellKey = `${row}-${c}`;
          if (hasAnyInRow) {
            newSelected.delete(cellKey);
          } else {
            newSelected.add(cellKey);
          }
        }
        setSelectedCells(newSelected);
      } else if (selectionMode === "column") {
        const newSelected = new Set(selectedCells);
        let hasAnyInCol = false;

        for (let r = 0; r < 20; r++) {
          if (newSelected.has(`${r}-${col}`)) {
            hasAnyInCol = true;
            break;
          }
        }

        for (let r = 0; r < 20; r++) {
          const cellKey = `${r}-${col}`;
          if (hasAnyInCol) {
            newSelected.delete(cellKey);
          } else {
            newSelected.add(cellKey);
          }
        }
        setSelectedCells(newSelected);
      } else if (selectionMode === "rectangle") {
        if (!isSelecting) {
          setIsSelecting(true);
          setSelectionStart({ row, col });
        } else {
          const newSelected = new Set(selectedCells);
          const minRow = Math.min(selectionStart.row, row);
          const maxRow = Math.max(selectionStart.row, row);
          const minCol = Math.min(selectionStart.col, col);
          const maxCol = Math.max(selectionStart.col, col);

          for (let r = minRow; r <= maxRow; r++) {
            for (let c = minCol; c <= maxCol; c++) {
              newSelected.add(`${r}-${c}`);
            }
          }

          setSelectedCells(newSelected);
          setIsSelecting(false);
          setSelectionStart(null);
        }
      }
    };

    const getCellStyle = (row, col) => {
      const key = `${row}-${col}`;
      const isSelected = selectedCells.has(key);
      const letter = grid[row][col];

      let bgColor = "bg-white";
      let borderStyle = "border border-gray-300";

      if (isSelected) {
        if (letter === "P") bgColor = "bg-red-200";
        else if (letter === "Q") bgColor = "bg-blue-200";
        else if (letter === "R") bgColor = "bg-green-200";
      }

      if (
        selectionMode === "rectangle" &&
        selectionStart &&
        selectionStart.row === row &&
        selectionStart.col === col
      ) {
        borderStyle = "border-4 border-orange-500";
        if (!isSelected) {
          bgColor = "bg-orange-100";
        }
      }

      if (selectionMode === "rectangle" && isSelecting && selectionStart) {
        const minRow = Math.min(selectionStart.row, row);
        const maxRow = Math.max(selectionStart.row, row);
        const minCol = Math.min(selectionStart.col, col);
        const maxCol = Math.max(selectionStart.col, col);

        if (row >= minRow && row <= maxRow && col >= minCol && col <= maxCol) {
          if (
            !isSelected &&
            !(selectionStart.row === row && selectionStart.col === col)
          ) {
            bgColor = "bg-yellow-100";
          }
        }
      }

      return `${bgColor} ${borderStyle} w-6 h-6 flex items-center justify-center text-xs font-bold cursor-pointer hover:bg-gray-100 transition-colors`;
    };

    const clearSelection = () => {
      setSelectedCells(new Set());
      setIsSelecting(false);
      setSelectionStart(null);
    };

    const handleModeChange = (newMode) => {
      if (selectionMode === "rectangle" && newMode !== "rectangle") {
        setIsSelecting(false);
        setSelectionStart(null);
      }
      setSelectionMode(newMode);
    };

    const cancelEqual = () => {
      const minCount = Math.min(counts.P, counts.Q, counts.R);
      if (minCount > 0) {
        const newCanceled = {
          P: canceledCounts.P + minCount,
          Q: canceledCounts.Q + minCount,
          R: canceledCounts.R + minCount,
        };
        setCanceledCounts(newCanceled);
      }
    };

    const resetCanceled = () => {
      setCanceledCounts({ P: 0, Q: 0, R: 0 });
    };

    const finalCounts = {
      P: counts.P - canceledCounts.P,
      Q: counts.Q - canceledCounts.Q,
      R: counts.R - canceledCounts.R,
    };

    const canCancel = Math.min(counts.P, counts.Q, counts.R) > 0;

    return (
      <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
        <div className="xl:col-span-3 bg-white rounded-xl shadow-lg p-6">
          <div className="mb-4">
            <h3 className="text-xl font-bold text-gray-800 mb-3">
              20×20 字母网格
            </h3>

            <div className="flex flex-wrap gap-2 mb-4">
              <button
                onClick={() => handleModeChange("cell")}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  selectionMode === "cell"
                    ? "bg-blue-500 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                <MousePointer size={16} />
                单选
              </button>
              <button
                onClick={() => handleModeChange("row")}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  selectionMode === "row"
                    ? "bg-blue-500 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                <Rows size={16} />
                整行
              </button>
              <button
                onClick={() => handleModeChange("column")}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  selectionMode === "column"
                    ? "bg-blue-500 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                <Columns size={16} />
                整列
              </button>
              <button
                onClick={() => handleModeChange("rectangle")}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  selectionMode === "rectangle"
                    ? "bg-blue-500 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                <Square size={16} />
                矩形
              </button>
              <button
                onClick={clearSelection}
                className="flex items-center gap-2 px-3 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 text-sm font-medium"
              >
                <RotateCcw size={16} />
                清除
              </button>
            </div>

            {selectionMode === "rectangle" && isSelecting && (
              <div className="mb-4 p-3 bg-yellow-100 rounded-lg border border-yellow-300">
                <p className="text-yellow-800 text-sm">
                  📍
                  矩形选择模式：起始点已标记（橙色边框），点击第二个角落完成选择
                </p>
              </div>
            )}
          </div>

          <div className="inline-block border-2 border-gray-400 bg-gray-100 p-2 rounded-lg">
            <div
              className="gap-0 bg-white"
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(20, 1fr)",
              }}
            >
              {grid.map((row, rowIndex) =>
                row.map((letter, colIndex) => (
                  <div
                    key={`${rowIndex}-${colIndex}`}
                    className={getCellStyle(rowIndex, colIndex)}
                    onClick={() => handleCellClick(rowIndex, colIndex)}
                  >
                    {letter}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
              <Grid3X3 className="text-blue-500" />
              统计结果
            </h3>

            <div className="space-y-3">
              <div className="flex justify-between items-center p-3 bg-red-50 rounded-lg">
                <span className="font-medium text-red-700">字母 P</span>
                <span className="text-2xl font-bold text-red-600">
                  {counts.P}
                </span>
              </div>
              <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                <span className="font-medium text-blue-700">字母 Q</span>
                <span className="text-2xl font-bold text-blue-600">
                  {counts.Q}
                </span>
              </div>
              <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                <span className="font-medium text-green-700">字母 R</span>
                <span className="text-2xl font-bold text-green-600">
                  {counts.R}
                </span>
              </div>

              <div className="pt-3 border-t border-gray-200">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">选中总数</span>
                  <span className="text-xl font-bold text-gray-800">
                    {counts.P + counts.Q + counts.R}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
              <Minus className="text-orange-500" />
              抵消功能
            </h3>

            <div className="space-y-3 mb-4">
              <div className="text-sm text-gray-600 mb-2">已抵消数量：</div>
              {canceledCounts.P > 0 ||
              canceledCounts.Q > 0 ||
              canceledCounts.R > 0 ? (
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>P: {canceledCounts.P}</span>
                    <span>Q: {canceledCounts.Q}</span>
                    <span>R: {canceledCounts.R}</span>
                  </div>
                </div>
              ) : (
                <p className="text-gray-500 text-sm">暂无抵消</p>
              )}

              <div className="pt-3 border-t border-gray-200">
                <div className="text-sm text-gray-600 mb-2">剩余数量：</div>
                <div className="flex justify-between">
                  <span className="text-red-600 font-bold">
                    P: {finalCounts.P}
                  </span>
                  <span className="text-blue-600 font-bold">
                    Q: {finalCounts.Q}
                  </span>
                  <span className="text-green-600 font-bold">
                    R: {finalCounts.R}
                  </span>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <button
                onClick={cancelEqual}
                disabled={!canCancel}
                className={`w-full py-2 px-4 rounded-lg font-medium transition-colors ${
                  canCancel
                    ? "bg-orange-500 text-white hover:bg-orange-600"
                    : "bg-gray-300 text-gray-500 cursor-not-allowed"
                }`}
              >
                抵消相等部分
              </button>
              <button
                onClick={resetCanceled}
                className="w-full py-2 px-4 bg-gray-500 text-white rounded-lg hover:bg-gray-600 font-medium"
              >
                重置抵消
              </button>
            </div>

            {canCancel && (
              <div className="mt-3 p-3 bg-orange-100 rounded-lg border border-orange-300">
                <p className="text-orange-800 text-sm">
                  💡 可以抵消 {Math.min(counts.P, counts.Q, counts.R)} 组 PQR
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // 主界面渲染
  const tabs = [
    {
      id: "circle",
      title: "Q7-1. 环形差值最大化",
      description: "将数字1-7填入圆圈，使相邻差值和最大",
      component: CircleDifferenceGame,
    },
    {
      id: "shoes",
      title: "Q7-2. 红色高帮鞋分配",
      description: "Jordan的15双鞋，找出红色高帮鞋最少数量",
      component: ShoeAllocationSimulator,
    },
    {
      id: "letters",
      title: "Q7-4. 字母统计问题",
      description: "20×20网格中统计P、Q、R字母数量",
      component: LetterGridCounter,
    },
  ];

  const ActiveComponent = tabs.find((tab) => tab.id === activeTab)?.component;

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-purple-100 p-4">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-5xl font-bold text-gray-800 mb-3">
            AMC数学竞赛模拟器套装
          </h1>
          <p className="text-gray-600 text-xl">
            交互式可视化工具，帮助理解和解决AMC数学竞赛问题
          </p>
        </div>

        {/* 标签页导航 */}
        <div className="flex flex-wrap justify-center gap-2 mb-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-6 py-4 rounded-xl text-left transition-all duration-300 transform hover:scale-105 ${
                activeTab === tab.id
                  ? "bg-blue-500 text-white shadow-lg"
                  : "bg-white text-gray-700 hover:bg-blue-50 shadow-md"
              }`}
            >
              <div className="font-bold text-lg">{tab.title}</div>
              <div
                className={`text-sm mt-1 ${
                  activeTab === tab.id ? "text-blue-100" : "text-gray-500"
                }`}
              >
                {tab.description}
              </div>
            </button>
          ))}
        </div>

        {/* 活动组件 */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {ActiveComponent && <ActiveComponent />}
        </div>

        {/* 底部说明 */}
        <div className="mt-8 text-center">
          <div className="bg-white rounded-xl shadow-lg p-6 max-w-4xl mx-auto">
            <h3 className="text-xl font-bold text-gray-800 mb-4">使用说明</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm text-gray-600">
              <div>
                <h4 className="font-semibold text-blue-600 mb-2">
                  Q7-1 环形差值
                </h4>
                <p>
                  通过拖拽数字到圆圈中，探索如何达到最大差值和24。学习组合优化和贪心策略。
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-red-600 mb-2">
                  Q7-2 鞋子问题
                </h4>
                <p>
                  手动分配红色鞋子，理解约束条件如何限制最优解。掌握集合论和最值问题。
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-green-600 mb-2">
                  Q7-4 字母统计
                </h4>
                <p>
                  选择不同区域统计字母，使用抵消功能简化计算。学习周期性模式和计数技巧。
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <footer className="mt-8 w-full text-center text-sm text-gray-500">
        © {new Date().getFullYear()} 孙维刚教育研究院-陈硕老师 保留所有权利。
      </footer>
    </div>
  );
};

export default AMCSimulatorSuite;
